/* eslint-disable no-param-reassign */
import { get } from 'lodash'

import { createBulkActionSlice, getInitialBulkActionState } from 'slices/generic/createBulkActionSlice'

const bulkRouteOptimizationSlice = createBulkActionSlice({
  name: 'bulkRouteOptimization',
  initialState: {
    ...getInitialBulkActionState(),
    skippedRouteStops: [], // Stops the optimizer could not sequence (CS-2929) — surfaced, never dropped
    showYardsOnMap: true, // Flag to control yard visibility on map
    isLoadingDirections: false, // Flag to track if directions API is loading
    directionsError: null, // Store any errors from the directions API
    shouldFetchDirections: false, // Flag to control when directions API should be called
    mapTypeId: 'roadmap',
    // Which plan is drawn, shared so the map's toggle and the single-list layout cannot disagree.
    comparisonView: 'both',
  },
  reducers: {},
})

export const {
  setBulkState: setBulkRouteOptimizationState,
  setBulkDate: setBulkRouteOptimizationDate,
  resetBulkState: resetBulkRouteOptimizationState,
} = bulkRouteOptimizationSlice.actions

// Stable reference so the map's colour lookup doesn't re-render on every unrelated dispatch.
const EMPTY_ROUTES_BY_ID = Object.freeze({})

const EMPTY_LEG_METERS = Object.freeze({})

// Selectors
export const selectIsOpenBulkRouteOptimizationDialog = state => get(state, 'bulkRouteOptimization.isOpen', false)
export const selectIsOpenBulkRouteOptimizationConfirmCloseDialog = state =>
  get(state, 'bulkRouteOptimization.isConfirmCloseDialogOpen', false)
export const selectBulkRouteOptimizationDate = state => get(state, 'bulkRouteOptimization.serializedServiceDate')
export const selectIsSelectDateDialogOpen = state => get(state, 'bulkRouteOptimization.isSelectDateDialogOpen', false)
export const selectBulkRouteOptimizationRouteId = state => get(state, 'bulkRouteOptimization.routeId', '')
export const selectBulkRouteOptimizationRoutesById = state => get(state, 'bulkRouteOptimization.routesById', EMPTY_ROUTES_BY_ID)
// Opened from the board with a job's result already staged, so its route and result are fixed.
export const selectIsBulkRouteOptimizationPreseeded = state => get(state, 'bulkRouteOptimization.isPreseeded', false)
// Run from the master column, so accepting it writes master and fans out to every future day.
export const selectBulkRouteOptimizationGridId = state => get(state, 'bulkRouteOptimization.gridId', '')
export const selectBulkRouteOptimizationJobId = state => get(state, 'bulkRouteOptimization.jobId', null)
export const selectIsBulkRouteOptimizationMasterRun = state => get(state, 'bulkRouteOptimization.isMasterRun', false)
export const selectBulkRouteOptimizationFetchId = state => get(state, 'bulkRouteOptimization.fetchId')
export const selectBulkRouteOptimizationRouteStops = state => get(state, 'bulkRouteOptimization.routeStops', [])
// The column `liveStopIds` describes — a later run must not read the last one's set.
export const selectBulkRouteOptimizationLiveStopIdsGridId = state => get(state, 'bulkRouteOptimization.liveStopIdsGridId')
// Published by a board column, which the master grid never holds; undefined means none has reported.
export const selectBulkRouteOptimizationLiveStopIds = state => get(state, 'bulkRouteOptimization.liveStopIds')
export const selectBulkRouteOptimizationOptimizedRouteStops = state => get(state, 'bulkRouteOptimization.optimizedRouteStops', [])
export const selectBulkRouteOptimizationSkippedRouteStops = state => get(state, 'bulkRouteOptimization.skippedRouteStops', [])
export const selectBulkRouteOptimizationMapCamera = state => get(state, 'bulkRouteOptimization.mapCamera')
// The stop the reader is pointing at, highlighted in both columns and centred on the map.
export const selectBulkRouteOptimizationSelectedStopId = state => get(state, 'bulkRouteOptimization.selectedStopId', null)
// Hover is a lighter touch than selection: it lights the row's ribbon without moving the camera.
export const selectBulkRouteOptimizationHoveredStopId = state => get(state, 'bulkRouteOptimization.hoveredStopId', null)
export const selectBulkRouteOptimizationMapTypeId = state => get(state, 'bulkRouteOptimization.mapTypeId', 'roadmap')
export const selectBulkRouteOptimizationComparisonView = state => get(state, 'bulkRouteOptimization.comparisonView', 'both')
export const selectBulkRouteOptimizationDirectionsData = state => get(state, 'bulkRouteOptimization.directionsData')
export const selectBulkRouteOptimizationIsPermanentMove = state => get(state, 'bulkRouteOptimization.isPermanentMove', false)
export const selectBulkRouteOptimizationStartingYard = state => get(state, 'bulkRouteOptimization.startingYard', null)
export const selectBulkRouteOptimizationEndingYard = state => get(state, 'bulkRouteOptimization.endingYard', null)
export const selectBulkRouteOptimizationShowYardsOnMap = state => get(state, 'bulkRouteOptimization.showYardsOnMap', true)
export const selectBulkRouteOptimizationOriginalRouteDistanceMeters = state =>
  get(state, 'bulkRouteOptimization.originalRouteDistanceMeters', 0)
export const selectBulkRouteOptimizationOptimizedRouteDistanceMeters = state =>
  get(state, 'bulkRouteOptimization.optimizedRouteDistanceMeters', 0)
export const selectBulkRouteOptimizationOriginalRouteDurationSeconds = state =>
  get(state, 'bulkRouteOptimization.originalRouteDurationSeconds', 0)
export const selectBulkRouteOptimizationOptimizedRouteDurationSeconds = state =>
  get(state, 'bulkRouteOptimization.optimizedRouteDurationSeconds', 0)
// The leg arriving at each stop, which is the per-row figure the result can actually back.
export const selectBulkRouteOptimizationOriginalRouteLegMeters = state =>
  get(state, 'bulkRouteOptimization.originalRouteLegMetersByStopId', EMPTY_LEG_METERS)
export const selectBulkRouteOptimizationOptimizedRouteLegMeters = state =>
  get(state, 'bulkRouteOptimization.optimizedRouteLegMetersByStopId', EMPTY_LEG_METERS)
export const selectBulkRouteOptimizationIsLoadingDirections = state => get(state, 'bulkRouteOptimization.isLoadingDirections', false)
export const selectBulkRouteOptimizationDirectionsError = state => get(state, 'bulkRouteOptimization.directionsError', null)
export const selectBulkRouteOptimizationShouldFetchDirections = state => get(state, 'bulkRouteOptimization.shouldFetchDirections', false)

export default bulkRouteOptimizationSlice.reducer
